import { ApiErrorBodySchema, type ErrorCode } from "@legacy-manager/shared";
import { z } from "zod";

/**
 * Cliente da API do modo online.
 *
 * Três decisões moram aqui, e as três existem para que as telas não precisem
 * pensar nelas:
 *
 * O **token de acesso vive na memória**, nunca em `localStorage`. Quem guarda o
 * refresh é o armazenamento seguro de cada plataforma, e isso é
 * responsabilidade de quem chama, não deste arquivo (docs/SECURITY.md 2).
 *
 * A **renovação é automática e acontece uma vez só**. Quando o token expira no
 * meio de várias chamadas simultâneas, todas esperam a mesma renovação em vez
 * de disparar uma cada.
 *
 * O **erro vira erro tipado**. A tela decide pelo código, nunca pela mensagem,
 * que pode mudar de redação ou de idioma.
 */

export class ErroDaApi extends Error {
  readonly code: ErrorCode | "REDE" | "INTERNAL";
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    code: ErroDaApi["code"],
    message: string,
    status: number,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ErroDaApi";
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** Sessão caiu: a tela deve mandar o jogador entrar de novo. */
  get precisaEntrar(): boolean {
    return this.code === "UNAUTHENTICATED" || this.code === "TOKEN_EXPIRED";
  }
}

export interface GuardaDeSessao {
  lerRefresh: () => Promise<string | null>;
  gravarRefresh: (token: string | null) => Promise<void>;
}

export interface OpcoesDoCliente {
  baseUrl: string;
  guarda: GuardaDeSessao;
  /** Versões enviadas em todo pedido, para o servidor recusar cliente velho. */
  clientVersion: string;
  engineVersion: string;
  /** Chamado quando a sessão morre de vez, para a tela reagir. */
  aoPerderSessao?: () => void;
}

interface Pedido {
  metodo: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  caminho: string;
  corpo?: unknown;
  /**
   * Corpo binário, para quando o que viaja não é JSON.
   *
   * Existe por causa do save na nuvem: `PUT /saves/:id` recebe o mundo
   * comprimido, alguns megabytes de bytes. Passar isso por JSON significaria
   * codificar em base64 e crescer um terço, para o servidor decodificar de
   * volta antes de gravar.
   *
   * Quando está presente, `corpo` é ignorado.
   */
  bytes?: Uint8Array;
  /** Tipo do corpo binário. O padrão serve para arquivo opaco. */
  tipoDoConteudo?: string;
  /**
   * Cabeçalhos que só este pedido usa.
   *
   * O save na nuvem precisa de `X-Save-Hash` e `If-Match`, que são a conversa
   * sobre qual versão está em dia. Sem eles, gravar save é sobrescrever no
   * escuro, e alguém perde a carreira.
   */
  cabecalhos?: Record<string, string>;
  /** Rota pública não tenta renovar token nem exige sessão. */
  publica?: boolean;
}

export class ClienteDaApi {
  private acesso: string | null = null;
  private renovacaoEmCurso: Promise<boolean> | null = null;

  constructor(private readonly opcoes: OpcoesDoCliente) {}

  get autenticado(): boolean {
    return this.acesso !== null;
  }

  definirAcesso(token: string | null): void {
    this.acesso = token;
  }

  /**
   * Faz o pedido, valida a resposta com o esquema e devolve o dado tipado.
   *
   * O esquema vem do pacote compartilhado, o mesmo que o servidor usa para
   * montar a resposta. Resposta fora do contrato vira erro aqui, e não uma
   * tela quebrada três componentes adiante.
   */
  async pedir<T>(pedido: Pedido, esquema: z.ZodType<T>): Promise<T> {
    const resposta = await this.enviar(pedido);
    if (resposta.status === 204) return esquema.parse(undefined);

    const texto = await resposta.text();
    const dados: unknown = texto ? JSON.parse(texto) : null;

    if (!resposta.ok) throw this.traduzirErro(dados, resposta.status);
    return esquema.parse(dados);
  }

  /**
   * Manda bytes e devolve o que voltou, já validado.
   *
   * O erro sai pelo mesmo caminho de sempre, com código estável e detalhes: é
   * justamente aqui que o 409 de conflito de save aparece, e a tela que trata
   * esse conflito precisa dos detalhes para desenhar a escolha.
   */
  async enviarBytes<T>(pedido: Pedido, esquema: z.ZodType<T>): Promise<T> {
    const resposta = await this.enviar(pedido);
    if (resposta.status === 204) return esquema.parse(undefined);

    const texto = await resposta.text();
    const dados: unknown = texto ? JSON.parse(texto) : null;

    if (!resposta.ok) throw this.traduzirErro(dados, resposta.status);
    return esquema.parse(dados);
  }

  /** Baixa um arquivo. Usado pelo save na nuvem, que volta como bytes. */
  async baixarBytes(pedido: Pedido): Promise<Uint8Array> {
    const resposta = await this.enviar(pedido);
    if (!resposta.ok) {
      const texto = await resposta.text();
      throw this.traduzirErro(texto ? JSON.parse(texto) : null, resposta.status);
    }
    return new Uint8Array(await resposta.arrayBuffer());
  }

  /** Para rotas que não devolvem corpo. */
  async pedirVazio(pedido: Pedido): Promise<void> {
    const resposta = await this.enviar(pedido);
    if (resposta.ok) return;
    const texto = await resposta.text();
    throw this.traduzirErro(texto ? JSON.parse(texto) : null, resposta.status);
  }

  private async enviar(pedido: Pedido, jaRenovou = false): Promise<Response> {
    const cabecalhos: Record<string, string> = {
      "X-Client-Version": this.opcoes.clientVersion,
      "X-Engine-Version": this.opcoes.engineVersion,
    };

    // O tipo de conteúdo só entra quando há conteúdo. Anunciar JSON e não mandar
    // nada é dizer uma coisa e fazer outra, e servidor nenhum é obrigado a
    // adivinhar qual das duas vale. Rota sem corpo (pedir ticket, sair da conta,
    // começar a liga) é chamada sem corpo, e é só isso que o cabeçalho diz.
    if (pedido.bytes !== undefined) {
      cabecalhos["Content-Type"] = pedido.tipoDoConteudo ?? "application/octet-stream";
    } else if (pedido.corpo !== undefined) {
      cabecalhos["Content-Type"] = "application/json";
    }
    if (this.acesso && !pedido.publica) cabecalhos["Authorization"] = `Bearer ${this.acesso}`;
    for (const [nome, valor] of Object.entries(pedido.cabecalhos ?? {})) cabecalhos[nome] = valor;

    let resposta: Response;
    try {
      resposta = await fetch(`${this.opcoes.baseUrl}${pedido.caminho}`, {
        method: pedido.metodo,
        headers: cabecalhos,
        body: corpoDoPedido(pedido),
        credentials: "include",
      });
    } catch {
      throw new ErroDaApi("REDE", "Não consegui falar com o servidor.", 0);
    }

    // Um 401 merece uma tentativa de renovação, e só uma: `jaRenovou` impede o
    // laço de quem renovou e continua recebendo 401.
    if (resposta.status === 401 && !pedido.publica && !jaRenovou) {
      const renovou = await this.renovar();
      if (renovou) return this.enviar(pedido, true);
      this.opcoes.aoPerderSessao?.();
    }

    return resposta;
  }

  /**
   * Renova o acesso, com ou sem token na mão.
   *
   * No navegador o refresh viaja em cookie `httpOnly`, então o guarda devolve
   * `null` sempre, e por isso desistir aí seria desistir justamente no caso mais
   * comum: a pessoa que fica horas com o jogo aberto na web nunca renovaria, e
   * o primeiro 401 a jogaria para fora. Sem token, o pedido vai com corpo vazio
   * e `credentials: "include"`, e quem responde é o cookie. Nos aplicativos
   * nativos o guarda tem o token de verdade e ele vai no corpo.
   *
   * Uma renovação por vez. Sem isso, dez chamadas simultâneas com token vencido
   * disparariam dez renovações, e nove invalidariam as outras, porque o refresh
   * gira a cada uso.
   */
  private async renovar(): Promise<boolean> {
    if (this.renovacaoEmCurso) return this.renovacaoEmCurso;

    this.renovacaoEmCurso = (async () => {
      const refresh = await this.opcoes.guarda.lerRefresh();

      try {
        const resposta = await fetch(`${this.opcoes.baseUrl}/v1/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(refresh ? { refreshToken: refresh } : {}),
          credentials: "include",
        });
        if (!resposta.ok) {
          await this.opcoes.guarda.gravarRefresh(null);
          this.acesso = null;
          return false;
        }
        const dados = (await resposta.json()) as { accessToken?: string; refreshToken?: string };
        if (!dados.accessToken) return false;
        this.acesso = dados.accessToken;
        if (dados.refreshToken) await this.opcoes.guarda.gravarRefresh(dados.refreshToken);
        return true;
      } catch {
        return false;
      } finally {
        this.renovacaoEmCurso = null;
      }
    })();

    return this.renovacaoEmCurso;
  }

  private traduzirErro(dados: unknown, status: number): ErroDaApi {
    const lido = ApiErrorBodySchema.safeParse(dados);
    if (lido.success) {
      return new ErroDaApi(
        lido.data.error.code,
        lido.data.error.message,
        status,
        lido.data.error.details as Record<string, unknown> | undefined,
      );
    }
    return new ErroDaApi("INTERNAL", "Algo deu errado no servidor.", status);
  }
}

/**
 * O corpo que vai na requisição.
 *
 * Bytes ganham de JSON quando os dois vierem, porque quem passa bytes está
 * mandando um arquivo e não um formulário.
 */
function corpoDoPedido(pedido: Pedido): BodyInit | undefined {
  if (pedido.bytes !== undefined) {
    // Uma cópia com bloco próprio: o `Uint8Array` que chega aqui pode estar
    // apoiado num bloco compartilhado, e o `fetch` recusa isso em alguns
    // motores em vez de mandar os bytes certos.
    const copia = new Uint8Array(new ArrayBuffer(pedido.bytes.byteLength));
    copia.set(pedido.bytes);
    return copia;
  }
  if (pedido.corpo === undefined) return undefined;
  return JSON.stringify(pedido.corpo);
}

/**
 * Guarda de sessão para o navegador.
 *
 * No web o refresh viaja em cookie `httpOnly`, então não há o que guardar aqui.
 * Nos aplicativos nativos esta interface é implementada pelo armazenamento
 * seguro do sistema, que é onde ele deve ficar.
 */
export const guardaDeNavegador: GuardaDeSessao = {
  lerRefresh: async () => null,
  gravarRefresh: async () => undefined,
};
